/**
 * Llamadas del circuito de acceso.
 *
 * Se hacen con `fetch` directo y NO con `lib/api`, por un motivo concreto: las
 * tres pantallas publicas (activar, recuperar) corren SIN sesion, y `lib/api`
 * adjunta el `Bearer` guardado y, ante un 401, dispara su cadena de refresco y
 * avisa que la sesion expiro. Con un token viejo en el `localStorage` —el caso
 * normal de quien vuelve a la pestana donde se le vencio la sesion— eso haria
 * que la pantalla de activacion mostrara «tu sesion expiro» en lugar de dejar a
 * la persona elegir su contrasena.
 *
 * La unica llamada que SI viaja autenticada es `cambiarMiContrasena`, y ahi se
 * pide el token al cliente real (`api.getAuthState()`) para no tener dos
 * fuentes de verdad.
 */

import { ApiError, api } from '@/lib/api';

const BASE = '/api/v1/acceso';

/** Motivo del enlace: primera vez, o recuperacion. Cambia el texto, no el circuito. */
export type MotivoAcceso = 'invitation' | 'reset';

export interface TokenVerificado {
  email: string;
  name: string;
  purpose: MotivoAcceso;
  expires_at: string;
  organization: string;
}

export interface Invitacion {
  user_id: number;
  email: string;
  name: string;
  /** El enlace para copiar. Existe SIEMPRE, con correo o sin correo. */
  link: string;
  /**
   * El mensaje ya redactado, listo para pegar en un WhatsApp. Lo arma el
   * backend porque nombra a la persona y a la administracion. Opcional para que
   * la pantalla no dependa de el: sin mensaje quedan el enlace y su boton.
   */
  share_text?: string;
  expires_at: string;
  purpose: MotivoAcceso;
  email_sent: boolean;
  email_error: string;
  email_configured: boolean;
  created: boolean;
}

export interface EstadoDeAcceso {
  user_id: number;
  email: string;
  tiene_contrasena: boolean;
  puede_entrar: boolean;
  invitacion_pendiente: boolean;
  invitacion_vence_el: string | null;
  invitacion_motivo: MotivoAcceso | null;
  /**
   * Cuando canjeo su ultimo enlace. NO es «ultimo ingreso»: con JWT el
   * `last_login` de Django no se escribe nunca, asi que ese dato no existe y
   * mostrarlo diria «nunca entro» de alguien que acaba de entrar.
   */
  eligio_clave_el: string | null;
}

export interface PadronDeAcceso {
  email_configured: boolean;
  invitation_days: number;
  reset_hours: number;
  results: EstadoDeAcceso[];
}

/**
 * Error del circuito, con el `code` que manda el backend.
 *
 * El codigo importa: `token_vencido` habilita el boton «pedir uno nuevo» y
 * `token_usado` ofrece ir al ingreso, mientras que un mensaje generico dejaria
 * a la persona sin saber que hacer.
 */
export class AccesoError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, message: string, code = '') {
    super(message);
    this.name = 'AccesoError';
    this.status = status;
    this.code = code;
  }
}

interface Opciones {
  autenticado?: boolean;
  metodo?: 'GET' | 'POST';
}

async function pedir<T>(ruta: string, cuerpo?: unknown, opciones: Opciones = {}): Promise<T> {
  const cabeceras: Record<string, string> = { Accept: 'application/json' };
  if (cuerpo !== undefined) cabeceras['Content-Type'] = 'application/json';

  if (opciones.autenticado === true) {
    const { access, companyId } = api.getAuthState();
    if (access !== null) cabeceras.Authorization = `Bearer ${access}`;
    if (companyId !== null) cabeceras['X-Company-Id'] = String(companyId);
  }

  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      method: opciones.metodo ?? 'POST',
      headers: cabeceras,
      credentials: 'same-origin',
      ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
    });
  } catch (error) {
    throw new AccesoError(0, 'No pudimos contactar al servidor. Revisá tu conexión.', 'sin_red');
  }

  const tipo = respuesta.headers.get('content-type') ?? '';
  const datos: unknown = tipo.includes('application/json') ? await respuesta.json() : null;

  if (!respuesta.ok) {
    const objeto = (datos ?? {}) as Record<string, unknown>;
    const detalle =
      typeof objeto.detail === 'string'
        ? objeto.detail
        : primerErrorDeCampo(objeto) ?? `El servidor respondió HTTP ${respuesta.status}.`;
    throw new AccesoError(
      respuesta.status,
      detalle,
      typeof objeto.code === 'string' ? objeto.code : '',
    );
  }

  return datos as T;
}

/** DRF devuelve `{campo: ["mensaje"]}` en las validaciones; se toma el primero. */
function primerErrorDeCampo(objeto: Record<string, unknown>): string | null {
  for (const valor of Object.values(objeto)) {
    if (Array.isArray(valor) && typeof valor[0] === 'string') return valor[0];
    if (typeof valor === 'string') return valor;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Publicas: sin sesion
// ---------------------------------------------------------------------------
export function verificarToken(token: string): Promise<TokenVerificado> {
  return pedir<TokenVerificado>('/verificar/', { token });
}

/**
 * Resultado del canje.
 *
 * `access` y `refresh` son el par de tokens con el que la pantalla deja a la
 * persona ADENTRO: el boton dice «Guardar y entrar» y eso es lo que tiene que
 * pasar. Son opcionales a proposito —si el servidor no los pudo emitir, la
 * contrasena igual quedo guardada— y la pantalla cae entonces al ingreso.
 */
export interface Activacion {
  email: string;
  detail: string;
  access?: string;
  refresh?: string;
}

export function activarConToken(
  token: string,
  password: string,
  password2: string,
): Promise<Activacion> {
  return pedir<Activacion>('/activar/', { token, password, password2 });
}

export function pedirRecuperacion(
  email: string,
): Promise<{ detail: string; email_configured: boolean }> {
  return pedir('/olvide/', { email });
}

// ---------------------------------------------------------------------------
// Con sesion
// ---------------------------------------------------------------------------
export function cambiarMiContrasena(
  actual: string,
  nueva: string,
  repeticion: string,
): Promise<{ detail: string; reauth_required: boolean }> {
  return pedir(
    '/cambiar/',
    { current_password: actual, new_password: nueva, new_password2: repeticion },
    { autenticado: true },
  );
}

// ---------------------------------------------------------------------------
// De administracion
// ---------------------------------------------------------------------------
export interface DatosDeInvitacion {
  email?: string;
  name?: string;
  nucleo_groups?: number[];
  companies?: number[];
  user_id?: number;
  enviar_mail?: boolean;
}

export function invitar(datos: DatosDeInvitacion): Promise<Invitacion> {
  return pedir<Invitacion>('/invitar/', datos, { autenticado: true });
}

export function forzarRestablecimiento(userId: number): Promise<Invitacion> {
  return pedir<Invitacion>('/restablecer/', { user_id: userId }, { autenticado: true });
}

export function leerPadronDeAcceso(): Promise<PadronDeAcceso> {
  return pedir<PadronDeAcceso>('/estado/', undefined, { autenticado: true, metodo: 'GET' });
}

export { ApiError };
